import { ShieldCheck, AlertTriangle, FileText, Hand, Heart, Flame } from "lucide-react";

export default function SafetyPage() {
  return (
    <div className="container-x py-16 lg:py-24" data-testid="safety-page">
      <div className="max-w-3xl">
        <div className="font-script text-2xl text-[#D97757]">safety & compliance</div>
        <h1 className="font-serif text-5xl lg:text-6xl mt-2 leading-tight">
          How we keep our friends safe.
        </h1>
        <p className="mt-6 text-lg text-[#5C4D43] leading-relaxed">
          Every Teddys Tiny Friend is hand-made to meet the requirements of UK toy
          safety law — the <strong>Toys (Safety) Regulations 2011</strong> and the relevant
          parts of the <strong>BS EN 71</strong> safety standards. Here&apos;s what that
          means in plain language, and the care we take with every stitch.
        </p>
      </div>

      <div className="mt-14 grid lg:grid-cols-2 gap-6">
        <SafetyCard
          icon={ShieldCheck}
          title="Toys (Safety) Regulations 2011"
          body="Our crochet animals are designed to comply with the UK Toys (Safety) Regulations 2011, which implement the requirements of EU Directive 2009/48/EC for the GB market. Each batch is documented and traceable."
        />
        <SafetyCard
          icon={FileText}
          title="BS EN 71 standards"
          body="We follow the relevant BS EN 71 toy safety standards: EN 71-1 (mechanical & physical properties), EN 71-2 (flammability) and EN 71-3 (migration of certain elements / chemical safety)."
        />
        <SafetyCard
          icon={Hand}
          title="Safety eyes & attachments"
          body="Where plastic safety eyes are used, they are fitted with a locking washer on the inside of the toy and pull-tested before despatch. All seams, limbs and attachments are reinforced to prevent loose small parts."
        />
        <SafetyCard
          icon={Heart}
          title="Materials"
          body="100% cotton or chenille yarn from OEKO-TEX certified suppliers where possible, and hi-loft polyester fibrefill stuffing. No glass eyes, no buttons, no detachable small parts."
        />
        <SafetyCard
          icon={Flame}
          title="Flammability — please read"
          body="Like most plush toys, our crochet animals contain hi-loft polyester fibrefill, which is a synthetic textile fibre and is FLAMMABLE if exposed to open flame. They are tested to meet the flammability requirements of BS EN 71-2 (the UK toy flammability standard), but they MUST still be kept well away from fires, candles, cookers, heaters and any other naked flame or extreme heat."
        />
      </div>

      <div className="mt-12 p-8 lg:p-10 rounded-3xl bg-[#F4EFE6]" data-testid="warning-card">
        <div className="flex items-start gap-4">
          <AlertTriangle className="text-[#D97757] shrink-0 mt-1" />
          <div>
            <div className="font-serif text-2xl">Age guidance & supervision</div>
            <p className="mt-3 text-[#5C4D43] leading-relaxed">
              Our toys that include plastic safety eyes carry the standard warning:
              <span className="block font-medium text-[#2C211B] mt-2">
                ⚠️ Warning: not suitable for children under 36 months. Small parts — choking hazard.
              </span>
              For children under 3, please choose one of our toys with fully embroidered features
              (eyes stitched directly into the yarn). In every case we recommend
              <span className="font-medium"> adult supervision during play</span>, and we ask
              that you regularly inspect the toy for any signs of wear, loose stitches, or damage.
              If anything comes loose, please stop use immediately and contact us — we&apos;ll
              repair or replace where possible.
            </p>
          </div>
        </div>
      </div>

      {/* Flammability warning */}
      <div className="mt-6 p-8 lg:p-10 rounded-3xl border-2 border-[#D97757]/30 bg-[#D97757]/5" data-testid="fire-warning-card">
        <div className="flex items-start gap-4">
          <Flame className="text-[#D97757] shrink-0 mt-1" />
          <div>
            <div className="font-serif text-2xl">Keep away from fire</div>
            <p className="mt-3 text-[#5C4D43] leading-relaxed">
              Our crochet animals are filled with <strong>hi-loft polyester fibrefill</strong> —
              the soft, fluffy stuffing standard in soft toys across the world. Polyester
              fibrefill is a synthetic textile and is <strong>flammable</strong>. Each toy
              is made to meet the flammability requirements of <strong>BS EN 71-2</strong>
              (the UK / EU toy flammability standard), and every toy ships with the legally
              required printed warning:
            </p>
            <div className="mt-4 p-4 rounded-xl bg-white border border-[#EAE3D9] font-medium text-[#2C211B]">
              ⚠️ Warning: Keep away from fire. Contains flammable materials.
            </div>
            <p className="mt-4 text-[#5C4D43] leading-relaxed text-sm">
              In practice that means please keep your little friend well away from candles,
              open fires, gas hobs, electric heaters, fireworks, lit cigarettes and any other
              naked flame or source of intense heat. Do not place near a radiator or in
              direct prolonged sunlight in a hot car. As with any soft toy, if it does come
              into contact with flame the polyester filling can ignite quickly — please treat
              ours with the same care you would give a duvet, pillow or any other home
              textile.
            </p>
          </div>
        </div>
      </div>

      <div className="mt-12 grid lg:grid-cols-2 gap-6">
        <div className="card-soft p-8">
          <div className="font-serif text-2xl">Markings & documentation</div>
          <p className="mt-3 text-[#5C4D43] leading-relaxed">
            Each toy ships with a printed swing tag showing the maker
            (Teddys Tiny Friends), batch identifier, materials, age guidance,
            the appropriate UKCA / CE marking, and the required <span className="font-medium">
            "Keep away from fire"</span> flammability warning. A written
            Declaration of Conformity and supporting technical file is kept
            for ten years from the date the toy was first placed on the market.
          </p>
        </div>
        <div className="card-soft p-8">
          <div className="font-serif text-2xl">Care & washing</div>
          <p className="mt-3 text-[#5C4D43] leading-relaxed">
            Surface-clean only — sponge with cool water and a tiny amount of
            gentle, fragrance-free detergent, then air-dry flat away from direct
            heat. Avoid the washing machine and tumble dryer; both can stretch
            the stitches and loosen the safety eyes.
          </p>
        </div>
      </div>

      <div className="mt-12 text-sm text-[#5C4D43] max-w-3xl leading-relaxed">
        <p>
          If you have any questions about the safety of a specific toy, or you&apos;d
          like a copy of the Declaration of Conformity for an item you&apos;ve received,
          please email us at{" "}
          <a className="underline" href="mailto:info@teddystinyfriends.co.uk">
            info@teddystinyfriends.co.uk
          </a>{" "}
          and we&apos;ll send it over. We take this seriously — these are toys made for
          small humans we love too.
        </p>
        <p className="mt-3">
          References: UK Toys (Safety) Regulations 2011 ·
          BS EN 71-1 (mechanical) / EN 71-2 (flammability) / EN 71-3 (chemical) ·
          UKCA &amp; CE marking guidance, gov.uk.
        </p>
      </div>
    </div>
  );
}

function SafetyCard({ icon: Icon, title, body }) {
  return (
    <div className="card-soft p-7" data-testid={`safety-card-${title.split(" ")[0].toLowerCase()}`}>
      <Icon className="text-[#D97757]" />
      <div className="font-serif text-2xl mt-4">{title}</div>
      <p className="mt-3 text-[#5C4D43] leading-relaxed">{body}</p>
    </div>
  );
}
